import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, PhoneCall, Star, ChevronRight, ArrowRight, Linkedin, CalendarClock, Mic, Users, Clock, CalendarDays, TrendingUp, FileEdit, SlidersHorizontal, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import CourseCard from "@/components/CourseCard";
import api from "@/lib/api";

const INK = "#0F1115";
const YELLOW = "#F5B301";

function SplitTitle({ black, red, center = false }) {
  return (
    <h2 className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${center ? "text-center" : ""}`} style={{ color: INK }}>
      {black} <span style={{ color: "#DB9E00" }}>{red}</span>
    </h2>
  );
}

function DHero({ c, cat, programs, onInquiry }) {
  const [expanded, setExpanded] = useState(false);
  const desc = c.description || "";
  const long = desc.length > 220;
  const scrollToPrograms = () => document.getElementById("domain-programs")?.scrollIntoView({ behavior: "smooth" });
  return (
    <section style={{ background: `linear-gradient(135deg, ${INK} 0%, #1A1D24 70%, #262A33 100%)` }} className="text-white" data-testid="d-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
        <Link to="/courses" className="text-slate-300 text-sm flex items-center gap-1.5 mb-6 hover:text-white" data-testid="d-back-to-catalog">← All programs</Link>
        <div className="flex items-center gap-4">
          <span className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: YELLOW, color: INK }}><GraduationCap className="w-7 h-7" /></span>
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="d-hero-title">{c.title || cat?.name}</h1>
            <p className="font-semibold mt-1" style={{ color: YELLOW }}>{c.tagline}</p>
          </div>
        </div>
        <p className="text-slate-300 mt-5 max-w-2xl leading-relaxed" data-testid="d-hero-desc">
          {long && !expanded ? `${desc.slice(0, 220)}…` : desc}
          {long && (
            <button onClick={() => setExpanded(!expanded)} className="ml-2 font-bold underline underline-offset-2" style={{ color: YELLOW }} data-testid="d-hero-readmore">
              {expanded ? "Read Less" : "Read More"}
            </button>
          )}
        </p>
        <div className="flex flex-wrap gap-2 mt-6">
          {(c.skills || []).map((s) => (
            <span key={s} className="text-xs font-semibold px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20" data-testid={`d-skill-${s.replace(/\s+/g, "-")}`}>{s}</span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-8">
          <button onClick={scrollToPrograms} className="rounded-xl font-bold py-3 px-7 text-sm transition-opacity hover:opacity-90" style={{ background: YELLOW, color: INK }} data-testid="d-explore-btn">
            {c.cta_primary || "Explore Now"}
          </button>
          <button onClick={onInquiry} className="rounded-xl font-bold py-3 px-6 text-sm border border-white/40 text-white hover:bg-white/10 transition-colors flex items-center gap-1.5" data-testid="d-counselor-btn">
            <PhoneCall className="w-4 h-4" /> {c.cta_secondary || "Get free counseling"}
          </button>
          <p className="text-slate-400 text-sm">{programs.length} program{programs.length === 1 ? "" : "s"} in this domain</p>
        </div>
      </div>
    </section>
  );
}

function DTopPrograms({ c, programs }) {
  const picks = (c.slugs || []).length
    ? (c.slugs || []).map((s) => programs.find((p) => p.slug === s)).filter(Boolean)
    : programs.slice(0, 3);
  if (!picks.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="d-top-programs">
      <SplitTitle black={c.title_black} red={c.title_red} />
      {c.subtitle && <p className="text-sm sm:text-base text-slate-500 mt-1">{c.subtitle}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {picks.slice(0, 3).map((p) => <CourseCard key={p.slug} program={p} />)}
      </div>
    </section>
  );
}

function DReviews({ c }) {
  if (!(c.reviews || []).length) return null;
  return (
    <section className="bg-slate-50 border-y border-slate-100" data-testid="d-reviews">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <SplitTitle black={c.title_black} red={c.title_red} center />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {(c.reviews || []).map((r, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg transition-shadow" data-testid={`d-review-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ background: "#FEF6E0", color: "#B45309" }}>{r.channel}</span>
                <span className="flex items-center gap-1 text-sm font-bold" style={{ color: INK }}><Star className="w-4 h-4" style={{ color: YELLOW }} fill={YELLOW} />{r.rating}</span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mt-4">"{r.text}"</p>
              <p className="text-xs font-bold text-slate-900 mt-4">— {r.author}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DEmployers({ c }) {
  const logos = c.logos || [];
  if (!logos.length) return null;
  const half = Math.ceil(logos.length / 2);
  const fill = (arr) => { let a = [...arr]; while (a.length && a.length < 6) a = [...a, ...arr]; return a.length ? a : logos; };
  const rowA = fill(logos.slice(0, half));
  const rowB = fill(logos.slice(half));
  return (
    <section className="bg-white border-b border-slate-100 py-14 overflow-hidden" data-testid="d-employers">
      <style>{`@keyframes emp-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } } .emp-marquee { animation: emp-marquee 36s linear infinite; } .emp-marquee:hover { animation-play-state: paused; } .emp-rev { animation-direction: reverse; } @media (prefers-reduced-motion: reduce) { .emp-marquee { animation: none; } }`}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SplitTitle black={c.title_black} red={c.title_red} center />
        {c.subtitle && <p className="text-sm sm:text-base text-slate-500 mt-2 text-center">{c.subtitle}</p>}
      </div>
      <div className="mt-10 flex flex-col gap-4">
        {[{ row: rowA, rev: false, key: "a" }, { row: rowB, rev: true, key: "b" }].map(({ row, rev, key }) => (
          <div key={key} className="overflow-hidden py-1" style={{ maskImage: "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)" }} data-testid={`d-employers-row-${key}`}>
            <div className={`flex items-center gap-4 w-max emp-marquee ${rev ? "emp-rev" : ""}`}>
              {[...row, ...row].map((l, i) => (
                <div key={i} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm shrink-0" data-testid={`d-employer-${key}-${i}`}>
                  {l.image ? (
                    <img src={l.image} alt={l.image_alt || l.name || "Employer logo"} title={l.image_title || l.name || ""} className="h-8 w-auto max-w-[110px] object-contain" />
                  ) : (
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-extrabold shrink-0 text-white" style={{ background: l.color || INK }}>
                      {(l.name || "CO").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                    </span>
                  )}
                  <span className="text-sm font-extrabold whitespace-nowrap" style={{ color: INK }}>{l.name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function DSkills({ c }) {
  if (!(c.skills || []).length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="d-skills">
      <SplitTitle black={c.title_black} red={c.title_red} />
      {c.subtitle && <p className="text-sm sm:text-base text-slate-500 mt-1">{c.subtitle}</p>}
      <div className="flex flex-wrap gap-3 mt-8">
        {(c.skills || []).map((s, i) => (
          <span key={i} className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-bold shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all" style={{ color: INK }} data-testid={`d-skill-chip-${i}`}>{s}</span>
        ))}
      </div>
    </section>
  );
}

function DPartners({ c }) {
  if (!(c.partners || []).length) return null;
  return (
    <section className="bg-slate-50 border-y border-slate-100" data-testid="d-partners">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <SplitTitle black={c.title_black} red={c.title_red} />
        {c.subtitle && <p className="text-sm sm:text-base text-slate-500 mt-1">{c.subtitle}</p>}
        <div className="flex flex-wrap gap-4 mt-8">
          {(c.partners || []).map((p, i) => (
            <div key={i} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm" data-testid={`d-partner-${i}`}>
              {p.logo ? (
                <img src={p.logo} alt={p.name} className="h-10 w-auto max-w-[90px] object-contain" />
              ) : (
                <span className="w-10 h-10 rounded-xl flex items-center justify-center font-extrabold text-sm" style={{ background: YELLOW, color: INK }}>
                  {(p.name || "TB").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </span>
              )}
              <p className="font-bold text-sm" style={{ color: INK }}>{p.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const pricingOf = (p) => p.pricing || (p.free ? "Free" : "Paid");
const levelOf = (p) => p.program_type || p.level || "";
const durationOf = (p) => p.duration_term || (p.duration_months ? `${p.duration_months} months` : "");
const partnerOf = (p) => p.partner || p.university || "";

const ordinal = (d) => { const s = ["th", "st", "nd", "rd"], v = d % 100; return d + (s[(v - 20) % 10] || s[v] || s[0]); };
const fmtCohort = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${ordinal(d.getDate())} ${d.toLocaleDateString("en-GB", { month: "short" })} '${String(d.getFullYear()).slice(2)}`;
};
const fmtLearners = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K+` : `${n}+`);

function DCourseRow({ p }) {
  const daysLeft = p.start_date ? Math.ceil((new Date(p.start_date) - Date.now()) / 86400000) : null;
  const partner = partnerOf(p);
  return (
    <Link to={`/courses/${p.slug}`} className="block rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`d-course-${p.slug}`}>
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <p className="text-xs text-slate-400 font-semibold">{p.program_type || p.level}</p>
          <h3 className="text-lg font-extrabold tracking-tight mt-1" style={{ color: INK }}>{p.title}</h3>
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-2.5 text-sm text-slate-600">
            {p.rating > 0 && <span className="flex items-center gap-1 font-semibold"><Star className="w-4 h-4" style={{ color: YELLOW }} fill={YELLOW} />{p.rating}</span>}
            {p.rating > 0 && <span className="text-slate-300">·</span>}
            {p.learners > 0 && <span className="flex items-center gap-1"><Users className="w-4 h-4 text-slate-400" />{fmtLearners(p.learners)}</span>}
            {p.learners > 0 && <span className="text-slate-300">·</span>}
            <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-slate-400" />{durationOf(p)}</span>
          </div>
          {p.start_date && (
            <p className="flex flex-wrap items-center gap-2 mt-2.5 text-sm text-slate-600">
              <CalendarDays className="w-4 h-4 text-slate-400" /> Cohort starts: <b style={{ color: INK }}>{fmtCohort(p.start_date)}</b>
              {daysLeft > 0 && daysLeft <= 30 && <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full" style={{ background: "#FEF6E0", color: "#B45309" }}>{daysLeft} Day{daysLeft === 1 ? "" : "s"} Left</span>}
            </p>
          )}
        </div>
        <div className="hidden sm:flex flex-col items-center justify-center gap-2 shrink-0 pl-6 border-l border-slate-100 min-w-[150px]">
          {p.logo ? (
            <img src={p.logo} alt={partner} className="h-12 w-auto max-w-[120px] object-contain" />
          ) : (
            <span className="w-12 h-12 rounded-xl flex items-center justify-center font-extrabold text-sm" style={{ background: YELLOW, color: INK }}>
              {(partner || "TB").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
            </span>
          )}
          <p className="text-sm font-bold text-center leading-snug" style={{ color: INK }}>{partner}</p>
        </div>
      </div>
    </Link>
  );
}

function DPrograms({ c, programs }) {
  const [f, setF] = useState({ pricing: [], level: [], duration: [], partner: [] });
  const [sheetOpen, setSheetOpen] = useState(false);
  const toggle = (k, v) => setF((prev) => ({ ...prev, [k]: prev[k].includes(v) ? prev[k].filter((x) => x !== v) : [...prev[k], v] }));
  const hasFilters = Object.values(f).some((a) => a.length);
  const activeCount = Object.values(f).reduce((n, a) => n + a.length, 0);
  const clear = () => setF({ pricing: [], level: [], duration: [], partner: [] });
  const filtered = programs.filter((p) =>
    (!f.pricing.length || f.pricing.includes(pricingOf(p))) &&
    (!f.level.length || f.level.includes(levelOf(p))) &&
    (!f.duration.length || f.duration.includes(durationOf(p))) &&
    (!f.partner.length || f.partner.includes(partnerOf(p))));
  const groups = [
    { key: "pricing", label: "Pricing", fn: pricingOf },
    { key: "level", label: "Program Level", fn: levelOf },
    { key: "duration", label: "Duration", fn: durationOf },
    { key: "partner", label: "Partner / Institution", fn: partnerOf },
  ];
  return (
    <section id="domain-programs" className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="d-programs">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <SplitTitle black={c.title_black} red={c.title_red} />
          {c.subtitle && <p className="text-sm sm:text-base text-slate-500 mt-1">{c.subtitle}</p>}
        </div>
        <p className="text-sm text-slate-400 font-semibold" data-testid="d-programs-count">{filtered.length} program{filtered.length === 1 ? "" : "s"}</p>
      </div>
      <div className="mt-6 flex items-center gap-2.5 lg:hidden" data-testid="d-mobile-toolbar">
        <button onClick={() => setSheetOpen(true)}
          className="relative inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-900 shadow-sm active:bg-slate-50 transition-colors"
          data-testid="d-filters-open">
          <SlidersHorizontal className="w-4 h-4" /> Filters
          {activeCount > 0 && (
            <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full text-white text-[10px] font-extrabold flex items-center justify-center" style={{ background: INK }} data-testid="d-filters-count">{activeCount}</span>
          )}
        </button>
        {hasFilters && <button onClick={clear} className="text-xs font-bold text-slate-500 hover:underline" data-testid="d-mobile-clear">Clear all</button>}
      </div>
      {hasFilters && (
        <div className="mt-3 flex flex-wrap gap-2 lg:hidden" data-testid="d-active-chips">
          {groups.flatMap((g) => f[g.key].map((o) => (
            <button key={`${g.key}-${o}`} onClick={() => toggle(g.key, o)}
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-900"
              data-testid={`d-chip-${g.key}-${o.replace(/\s+/g, "-")}`}>
              {o} <X className="w-3 h-3" />
            </button>
          )))}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 mt-8">
        <aside className="hidden lg:block rounded-2xl border border-slate-200 bg-white self-start lg:sticky lg:top-6" data-testid="d-filter-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <p className="text-lg font-extrabold" style={{ color: INK }}>Filters</p>
            {hasFilters && <button onClick={clear} className="text-xs font-bold hover:underline" style={{ color: "#B45309" }} data-testid="d-clear-filters">Clear all</button>}
          </div>
          {groups.map((g) => {
            const options = [...new Set(programs.map(g.fn).filter(Boolean))].sort();
            if (!options.length) return null;
            return (
              <div key={g.key} className="px-5 py-4 border-b border-slate-100 last:border-0" data-testid={`d-filter-group-${g.key}`}>
                <p className="text-sm font-semibold text-slate-700 mb-1.5">{g.label}</p>
                {options.map((o) => {
                  const count = programs.filter((p) => g.fn(p) === o).length;
                  return (
                    <label key={o} className="flex items-center gap-2.5 py-1.5 cursor-pointer text-sm text-slate-600 hover:text-slate-900" data-testid={`d-filter-${g.key}-${o.replace(/\s+/g, "-")}`}>
                      <input type="checkbox" checked={f[g.key].includes(o)} onChange={() => toggle(g.key, o)} className="w-4 h-4 rounded accent-[#B45309]" />
                      <span className="flex-1">{o}</span>
                      <span className="text-xs text-slate-400">({count})</span>
                    </label>
                  );
                })}
              </div>
            );
          })}
        </aside>
        <div className="flex flex-col gap-5">
          {filtered.map((p) => <DCourseRow key={p.slug} p={p} />)}
          {filtered.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 p-14 text-center bg-white" data-testid="d-programs-empty">
              <p className="font-semibold text-slate-700">{programs.length ? "No programs match these filters" : "Programs for this domain are being finalized"}</p>
              <p className="text-sm sm:text-base text-slate-500 mt-1">{programs.length ? "Try clearing a filter." : "Request a callback and we'll notify you first."}</p>
            </div>
          )}
        </div>
      </div>
      <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
        <DrawerContent className="lg:hidden rounded-t-3xl max-h-[85vh] bg-white" data-testid="d-filter-sheet">
          <DrawerHeader className="flex flex-row items-center justify-between border-b border-slate-100 px-5 py-4 text-left">
            <DrawerTitle className="text-lg font-extrabold text-slate-900">Filter programs</DrawerTitle>
            <DrawerClose asChild>
              <button className="p-2 text-slate-400 hover:text-slate-700 rounded-lg" data-testid="d-sheet-close" aria-label="Close filters"><X className="w-5 h-5" /></button>
            </DrawerClose>
          </DrawerHeader>
          <div className="overflow-y-auto px-5 py-5 flex flex-col gap-6">
            {groups.map((g) => {
              const options = [...new Set(programs.map(g.fn).filter(Boolean))].sort();
              if (!options.length) return null;
              return (
                <div key={g.key} data-testid={`d-sheet-group-${g.key}`}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2.5">{g.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {options.map((o) => {
                      const on = f[g.key].includes(o);
                      const count = programs.filter((p) => g.fn(p) === o).length;
                      return (
                        <button key={o} onClick={() => toggle(g.key, o)}
                          className={`px-3.5 py-2 rounded-full text-sm font-semibold border transition-colors ${on ? "border-transparent text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                          style={on ? { background: INK } : {}}
                          data-testid={`d-sheet-${g.key}-${o.replace(/\s+/g, "-")}`}>
                          {o} <span className={on ? "text-white/60" : "text-slate-400"}>({count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 border-t border-slate-100 px-5 py-4" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
            <button onClick={clear} className="rounded-xl flex-1 border border-slate-200 font-bold text-slate-700 py-3" data-testid="d-sheet-clear">Clear all</button>
            <button onClick={() => setSheetOpen(false)} className="rounded-xl flex-1 font-extrabold py-3" style={{ background: YELLOW, color: INK }} data-testid="d-sheet-apply">
              Apply filters{filtered.length ? ` (${filtered.length})` : ""}
            </button>
          </div>
        </DrawerContent>
      </Drawer>
    </section>
  );
}

function DOverview({ c }) {
  const topics = (c.topics || []).filter((t) => t.label);
  const [active, setActive] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const paras = c.paragraphs || [];
  const cur = topics[active] || {};
  const bullets = String(cur.bullets || "").split("\n").map((b) => b.trim()).filter(Boolean).map((b) => {
    const [text, link] = b.split("|").map((x) => (x || "").trim());
    return { text, link };
  });
  if (!topics.length && !paras.length) return null;
  return (
    <section className="bg-slate-50 border-y border-slate-100" data-testid="d-overview">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        {(c.title_black || c.title_red) && <SplitTitle black={c.title_black} red={c.title_red} />}
        {topics.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden mt-8 grid grid-cols-1 md:grid-cols-[300px_1fr]" data-testid="d-overview-topics">
            <div className="bg-slate-50/80 border-b md:border-b-0 md:border-r border-slate-200 flex md:flex-col overflow-x-auto">
              {topics.map((t, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`d-topic-${i}`}
                  className={`text-left text-sm font-semibold px-5 py-4 whitespace-nowrap md:whitespace-normal transition-colors shrink-0 md:shrink ${i === active ? "bg-white border-l-4 font-bold" : "border-l-4 border-transparent text-slate-600 hover:bg-white/60"}`}
                  style={i === active ? { color: "#B45309", borderColor: YELLOW } : {}}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="p-6 sm:p-8" data-testid="d-topic-pane">
              {cur.intro && <p className="text-slate-600 leading-relaxed">{cur.intro}</p>}
              {bullets.length > 0 && (
                <ul className="mt-4 flex flex-col gap-3">
                  {bullets.map((b, i) => (
                    <li key={i} className="flex gap-2.5 text-slate-700" data-testid={`d-topic-bullet-${i}`}>
                      <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: YELLOW }} />
                      {b.link ? (
                        <a href={b.link} className="font-semibold hover:underline" style={{ color: "#B45309" }}>{b.text}</a>
                      ) : (
                        <span>{b.text}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        {paras.length > 0 && (
          <div className="max-w-4xl mt-10">
            {(c.more_title_black || c.more_title_red) && <SplitTitle black={c.more_title_black} red={c.more_title_red} />}
            <div className="mt-4 text-slate-600 leading-relaxed flex flex-col gap-4">
              <p>{paras[0]}</p>
              {expanded && paras.slice(1).map((p, i) => <p key={i}>{p}</p>)}
            </div>
            {paras.length > 1 && (
              <button onClick={() => setExpanded(!expanded)} className="mt-4 text-sm font-bold hover:underline" style={{ color: "#B45309" }} data-testid="d-overview-readmore">
                {expanded ? "Show Less" : "Know More"} <ArrowRight className={`inline w-4 h-4 transition-transform ${expanded ? "-rotate-90" : "rotate-90"}`} />
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function DCareer({ c }) {
  const stats = c.stats || [];
  if (!stats.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="d-career">
      <SplitTitle black={c.title_black} red={c.title_red} />
      {c.subtitle && <p className="text-sm sm:text-base text-slate-500 mt-1 max-w-3xl">{c.subtitle}</p>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-8">
        {stats.map((s, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-7 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`d-stat-${i}`}>
            <span className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center" style={{ background: "#22C55E" }}>
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </span>
            <p className="text-xl sm:text-3xl font-extrabold tracking-tight mt-4 sm:mt-6" style={{ color: INK }}>{s.value}</p>
            <p className="text-xs sm:text-sm sm:text-base text-slate-500 mt-1.5">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function DInquiry({ c, onInquiry }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14" data-testid="d-inquiry">
      <div className="rounded-3xl p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" style={{ background: `linear-gradient(120deg, ${INK}, #262A33)` }}>
        <div>
          <h3 className="text-2xl font-extrabold text-white tracking-tight">{c.headline}</h3>
          <p className="text-slate-300 text-sm mt-2 max-w-xl">{c.subtext}</p>
        </div>
        <button onClick={onInquiry} className="rounded-xl font-bold py-3.5 px-7 text-sm shrink-0 transition-opacity hover:opacity-90" style={{ background: YELLOW, color: INK }} data-testid="d-inquiry-cta">
          {c.cta_label || "Request a callback"}
        </button>
      </div>
    </section>
  );
}

function ToolLogo({ t, i }) {
  if (t.logo) return <img src={t.logo} alt={t.name || "Tool"} className="h-9 w-auto max-w-[130px] object-contain" data-testid={`d-tool-logo-${i}`} />;
  return <span className="text-xl sm:text-2xl font-extrabold whitespace-nowrap tracking-tight" style={{ color: t.color || INK }} data-testid={`d-tool-${i}`}>{t.name}</span>;
}

function DTools({ c }) {
  const tools = c.tools || [];
  const [showAll, setShowAll] = useState(false);
  if (!tools.length) return null;
  const LIMIT = 6;
  const many = tools.length > LIMIT;
  const shown = tools.slice(0, LIMIT);
  const hasTitle = c.title_black || c.title_red || c.subtitle;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="d-tools">
      <style>{`@keyframes dt-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } } .dt-marquee { animation: dt-marquee 26s linear infinite; } .dt-marquee:hover { animation-play-state: paused; }`}</style>
      <div className={hasTitle ? "grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-10 items-start" : ""}>
        {hasTitle && (
          <div>
            <SplitTitle black={c.title_black} red={c.title_red} />
            {c.subtitle && <p className="text-sm sm:text-base text-slate-500 mt-3 leading-relaxed">{c.subtitle}</p>}
          </div>
        )}
        <div className="min-w-0">
          {showAll ? (
            <div className="flex flex-wrap items-center gap-x-10 gap-y-6" data-testid="d-tools-list">
              {tools.map((t, i) => <ToolLogo key={i} t={t} i={i} />)}
            </div>
          ) : (
            <div className="overflow-hidden py-1" data-testid="d-tools-slider" style={{ maskImage: "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)" }}>
              <div className="flex items-center gap-10 w-max dt-marquee">
                {[...shown, ...shown].map((t, i) => <ToolLogo key={i} t={t} i={i % shown.length} />)}
              </div>
            </div>
          )}
          {many && (
            <button onClick={() => setShowAll(!showAll)} className="mt-5 text-sm font-bold hover:underline" style={{ color: "#2563EB" }} data-testid="d-tools-view-toggle">
              {showAll ? "View Less" : "View All"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function DWebinars({ c }) {
  if (!(c.webinars || []).length) return null;
  return (
    <section className="bg-slate-50 border-y border-slate-100" data-testid="d-webinars">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <SplitTitle black={c.title_black} red={c.title_red} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {(c.webinars || []).map((w, i) => {
            const inner = (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full hover:shadow-lg transition-shadow" data-testid={`d-webinar-${i}`}>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ background: "#FEF6E0", color: "#B45309" }}><Mic className="w-3 h-3" /> Webinar</span>
                <p className="font-bold mt-3" style={{ color: INK }}>{w.title}</p>
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" />{w.date}{w.speaker ? ` · ${w.speaker}` : ""}</p>
              </div>
            );
            return w.link ? <a key={i} href={w.link} target="_blank" rel="noreferrer">{inner}</a> : <div key={i}>{inner}</div>;
          })}
        </div>
      </div>
    </section>
  );
}

function DArticles({ c }) {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/posts").then((r) => setPosts(r.data.posts || [])).catch(() => {}); }, []);
  const count = parseInt(c.count) || 3;
  const shown = posts.slice(0, count);
  if (!shown.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="d-articles">
      <SplitTitle black={c.title_black} red={c.title_red} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        {shown.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`d-article-${p.slug}`}>
            {(p.cover_image || p.image) && <img src={p.cover_image || p.image} alt={p.title} className="h-40 w-full object-cover" />}
            <div className="p-5">
              <p className="font-bold leading-snug" style={{ color: INK }}>{p.title}</p>
              {(p.excerpt || p.summary) && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{p.excerpt || p.summary}</p>}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function DAdmission({ c }) {
  const steps = c.steps || [];
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  if (!steps.length) return null;
  return (
    <section ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="d-admission">
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ color: INK }} data-testid="d-admission-title">
        <span style={{ color: "#E14B3A" }}>{c.title_red}</span> {c.title_black}
      </h2>
      {c.subtitle && <p className="text-sm sm:text-base text-slate-500 mt-2 max-w-2xl" data-testid="d-admission-subtitle">{c.subtitle}</p>}
      {(c.intro_label || c.intro_text) && (
        <div className="flex items-start gap-4 mt-8" data-testid="d-admission-intro">
          <span className="w-14 h-14 rounded-full flex items-center justify-center shrink-0" style={{ background: "#FDECEA" }}>
            <FileEdit className="w-6 h-6" style={{ color: "#E14B3A" }} />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-500">{c.intro_label}</p>
            <p className="font-extrabold mt-0.5" style={{ color: INK }}>{c.intro_text}</p>
          </div>
        </div>
      )}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
        {steps.map((st, i) => (
          <div key={i} className="relative" data-testid={`d-admission-step-${i}`}
            style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transition: `opacity 0.5s ease ${i * 0.18}s, transform 0.5s ease ${i * 0.18}s` }}>
            {i > 0 && (
              <span className="hidden lg:flex items-center absolute -left-7 top-16" aria-hidden>
                <span className="w-4 border-t-2 border-dashed border-slate-300" />
                <ChevronRight className="w-4 h-4 text-slate-300 -ml-1" />
              </span>
            )}
            <div className="h-24 flex items-end">{st.image && <img src={st.image} alt="" className="h-24 w-auto object-contain rounded-lg" />}</div>
            <p className="font-extrabold text-lg mt-4" style={{ color: INK }}>{st.title}</p>
            <p className="text-sm sm:text-base text-slate-500 mt-1.5 leading-relaxed">{st.text}</p>
          </div>
        ))}
      </div>
      {c.cta_label && (
        <div className="mt-10">
          <Link to={c.cta_link || "/courses"} className="inline-flex items-center gap-2 px-7 h-12 rounded-xl font-bold text-white shadow-lg hover:opacity-90 transition-opacity" style={{ background: "#E14B3A" }} data-testid="d-admission-cta">
            {c.cta_label} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </section>
  );
}

function DWhy({ c }) {
  if (!(c.cards || []).length) return null;
  return (
    <section className="text-white" style={{ background: INK }} data-testid="d-why">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-center">{c.title_black} <span style={{ color: YELLOW }}>{c.title_red}</span></h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {(c.cards || []).map((card, i) => (
            <div key={i} className="rounded-2xl p-6 border border-white/10" style={{ background: "#1A1D24" }} data-testid={`d-why-${i}`}>
              <p className="font-extrabold" style={{ color: YELLOW }}>{card.title}</p>
              <p className="text-sm text-slate-300 leading-relaxed mt-2">{card.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DMentors({ c }) {
  if (!(c.mentors || []).length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="d-mentors">
      <SplitTitle black={c.title_black} red={c.title_red} center />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
        {(c.mentors || []).map((m, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 text-center hover:shadow-lg transition-shadow" data-testid={`d-mentor-${i}`}>
            {m.image ? (
              <img src={m.image} alt={m.name} className="w-20 h-20 rounded-full object-cover mx-auto border-2" style={{ borderColor: YELLOW }} />
            ) : (
              <span className="w-20 h-20 rounded-full mx-auto flex items-center justify-center font-extrabold text-xl" style={{ background: "#FEF6E0", color: "#B45309" }}>
                {(m.name || "M").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </span>
            )}
            <p className="font-extrabold mt-4" style={{ color: INK }}>{m.name}</p>
            <p className="text-xs font-semibold mt-0.5" style={{ color: "#B45309" }}>{m.role}{m.company ? ` · ${m.company}` : ""}</p>
            {m.linkedin && <a href={m.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold mt-3 hover:underline" style={{ color: INK }}><Linkedin className="w-3.5 h-3.5" /> LinkedIn Profile</a>}
          </div>
        ))}
      </div>
    </section>
  );
}

function DFaqs({ c }) {
  if (!(c.faqs || []).length) return null;
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16" data-testid="d-faqs">
      <SplitTitle black={c.title_black} red={c.title_red} center />
      <div className="mt-8">
        <Accordion type="single" collapsible>
          {(c.faqs || []).map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`d-faq-${i}`}>
              <AccordionTrigger className="text-left font-semibold text-slate-800">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function DCategories({ c, currentSlug }) {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data.categories || [])).catch(() => {}); }, []);
  const others = cats.filter((x) => x.slug !== currentSlug);
  if (!others.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14" data-testid="d-categories">
      <h2 className="text-xl font-extrabold tracking-tight" style={{ color: INK }}>{c.title || "Explore Other Categories"}</h2>
      <div className="flex flex-wrap gap-3 mt-5">
        {others.map((x) => (
          <Link key={x.slug} to={`/domains/${x.slug}`} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-bold shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all" style={{ color: INK }} data-testid={`d-category-${x.slug}`}>
            {x.name} <ChevronRight className="w-3.5 h-3.5" style={{ color: "#B45309" }} />
          </Link>
        ))}
      </div>
    </section>
  );
}

function DDisclaimer({ c }) {
  const [expanded, setExpanded] = useState(false);
  const paras = c.paragraphs || [];
  if (!paras.length) return null;
  return (
    <section className="border-t border-slate-100" data-testid="d-disclaimer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs font-extrabold uppercase tracking-widest text-slate-400">{c.title || "Disclaimer"}</p>
        <div className="text-xs text-slate-500 leading-relaxed mt-3 flex flex-col gap-2">
          <p>{expanded ? paras.join(" ") : paras[0]}</p>
        </div>
        {paras.length > 1 && (
          <button onClick={() => setExpanded(!expanded)} className="text-xs font-bold mt-2 hover:underline" style={{ color: "#B45309" }} data-testid="d-disclaimer-toggle">
            {expanded ? "Read Less" : "Read More"}
          </button>
        )}
      </div>
    </section>
  );
}

export default function DomainTemplateRenderer({ sections, category, programs = [], onInquiry, device }) {
  return (
    <div className="bg-white" data-testid="domain-template-renderer">
      {(sections || []).filter((s) => {
        if (s.enabled === false) return false;
        if (device === "mobile" && s.hide_mobile) return false;
        if (device === "desktop" && s.hide_desktop) return false;
        return true;
      }).map((s) => {
        const c = s.content || {};
        let node = null;
        switch (s.type) {
          case "d_hero": node = <DHero c={c} cat={category} programs={programs} onInquiry={onInquiry} />; break;
          case "d_top_programs": node = <DTopPrograms c={c} programs={programs} />; break;
          case "d_reviews": node = <DReviews c={c} />; break;
          case "d_employers": node = <DEmployers c={c} />; break;
          case "d_skills": node = <DSkills c={c} />; break;
          case "d_partners": node = <DPartners c={c} />; break;
          case "d_programs": node = <DPrograms c={c} programs={programs} />; break;
          case "d_overview": node = <DOverview c={c} />; break;
          case "d_career": node = <DCareer c={c} />; break;
          case "d_admission": node = <DAdmission c={c} />; break;
          case "d_inquiry": node = <DInquiry c={c} onInquiry={onInquiry} />; break;
          case "d_tools": node = <DTools c={c} />; break;
          case "d_webinars": node = <DWebinars c={c} />; break;
          case "d_articles": node = <DArticles c={c} />; break;
          case "d_why": node = <DWhy c={c} />; break;
          case "d_mentors": node = <DMentors c={c} />; break;
          case "d_faqs": node = <DFaqs c={c} />; break;
          case "d_categories": node = <DCategories c={c} currentSlug={category?.slug} />; break;
          case "d_disclaimer": node = <DDisclaimer c={c} />; break;
          default: break;
        }
        if (!node) return null;
        const hideCls = device ? "" : s.hide_mobile ? "hidden md:block" : s.hide_desktop ? "md:hidden" : "";
        return <div key={s.id} className={hideCls || undefined}>{node}</div>;
      })}
    </div>
  );
}
